/**
 * Transforms prompt answers for API submission.
 * @param {Array} promptAnswers - The array of prompt answers to transform.
 * @returns {Array} Transformed prompt answers ready for API submission.
 */
export const transformPromptAnswers = (promptAnswers) => {
  const preparedAnswers = promptAnswers.reduce((acc, { field, values }) => {
    if (Array.isArray(values)) {
      if (acc[field]) {
        acc[field] = [...acc[field], ...values];
      } else {
        acc[field] = values;
      }
    } else if (typeof values === 'object' && values.date_range) {
      acc[field] = values.date_range;
    }
    return acc;
  }, {});

  return {
    ...preparedAnswers,
  };
};

const createGroupedPrompt = ({ groupedPrompts, groupingId, groupingIndex, groupingName }) => ({
  attributes: groupedPrompts.flatMap((prompt) => prompt.attributes || []),
  description: null,
  emptyValue: `Any ${groupingName || 'Prompt'}`,
  groupedPrompts,
  hasCustomDateOption: false,
  id: groupingId || `prompt_grouping_${groupingIndex}`,
  isAdvanced: false,
  isDateRange: false,
  isPinnable: true,
  isPrefixIncluded: true,
  isReadOnly: false,
  isRequired: false,
  isSearchEnabled: true,
  isSortEnabled: false,
  maxValues: null,
  minAnswers: null,
  name: groupingName || '',
  promptKey: groupingId || `prompt_grouping_${groupingIndex}`,
  shortTitle: `Select ${groupingName || ''}`,
  title: `Select ${groupingName || ''}`,
  type: 10,
});

/** * Filters prompts based on the narrative ID.
 * @param {Object[]} prompts - The array of prompts to filter.
 * @param {Object} promptGrouping - The prompt grouping object containing prompt name and grouped prompt IDs.
 * @returns {Object[]} Filtered prompts.
 */
export const filterPrompts = (prompts, promptGrouping) => {
  if (!Array.isArray(prompts)) return [];
  const getPromptKey = (prompt) => prompt?.promptKey || prompt?.id;
  const isVisiblePrompt = (prompt) => prompt?.isAdvanced !== true;

  const visiblePrompts = prompts.filter(isVisiblePrompt);
  const groupings = Array.isArray(promptGrouping)
    ? promptGrouping
    : promptGrouping
      ? [promptGrouping]
      : [];

  if (groupings.length === 0) return visiblePrompts;

  const promptByKey = new Map(prompts.map((prompt) => [getPromptKey(prompt), prompt]));
  const groupedPromptsByFirstIndex = new Map();
  const groupedPromptKeys = new Set();

  groupings.forEach((grouping, groupingIndex) => {
    const groupedPrompts = (grouping?.grouped_prompt_ids || [])
      .map((promptKey) => promptByKey.get(promptKey))
      .filter(
        (prompt) => prompt && !prompt?.isRequired && !groupedPromptKeys.has(getPromptKey(prompt))
      );

    if (groupedPrompts.length === 0) return;

    groupedPrompts.forEach((prompt) => groupedPromptKeys.add(getPromptKey(prompt)));

    const firstGroupedPromptIndex = prompts.findIndex(
      (prompt) => getPromptKey(prompt) === getPromptKey(groupedPrompts[0])
    );

    groupedPromptsByFirstIndex.set(
      firstGroupedPromptIndex,
      createGroupedPrompt({
        groupedPrompts,
        groupingId: grouping?.id,
        groupingIndex,
        groupingName: grouping?.name,
      })
    );
  });

  return prompts.reduce((filteredPrompts, prompt, index) => {
    if (groupedPromptsByFirstIndex.has(index)) {
      filteredPrompts.push(groupedPromptsByFirstIndex.get(index));
    }

    if (isVisiblePrompt(prompt) && !groupedPromptKeys.has(getPromptKey(prompt))) {
      filteredPrompts.push(prompt);
    }

    return filteredPrompts;
  }, []);
};
